import { Form, InputNumber, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import { getPreviousStep, getNextStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS, FAMILY_BRANCH_MAP, FAMILY_COMPONENT_MAP } from '../../familyApplicationConstants';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { familyService, commonService } from 'services';
import Select from 'components/Select';
import { useAsync } from 'react-async-hook';
import { Checkbox } from 'antd';
import { DatePicker } from 'components/ui/DatePicker';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyAppSpouseMilitaryEmployment({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  // Application state
  const [submitting, setSubmitting] = useState(false);
  const isCompletedBySponsor = application?.completedBySponsor === true;
  const spouse = useMemo(() => application?.additionalParents?.[0] || {}, [application?.additionalParents]);

  // Form state
  const [form] = Form.useForm();
  const branchFormValue = Form.useWatch(['militaryDetails', 'branch'], form);
  const componentFormValue = Form.useWatch(['militaryDetails', 'militaryComponent'], form);
  const statusFormValue = Form.useWatch(['militaryDetails', 'militaryStatus'], form);
  const dontKnowStartDateFormValue = Form.useWatch('orderStartDateUnknown', form);
  const dontKnowEndDateFormValue = Form.useWatch('orderEndDateUnknown', form);

  // API fetch for branch options
  const { result: branchOptions = [] } = useAsync(async () => {
    const _branches = await commonService.get('/families/ref-data/military-info/branches?includeAll=true', {
      cache: { interpretHeader: false },
    });

    return _branches.filter((_branch) => _branch.id !== FAMILY_BRANCH_MAP.departmentOfDefense.id);
  }, []);

  // API fetches for component options
  const { result: componentOptions = [] } = useAsync(async () => {
    if (!branchFormValue?.id) return [];

    return commonService.get(`/families/ref-data/military-info/components?branchId=${branchFormValue.id}`, {
      cache: { interpretHeader: false },
    });
  }, [branchFormValue?.id]);

  // API fetch for statuses based on component selection
  const { result: statusOptions = [] } = useAsync(async () => {
    if (!componentFormValue?.id) return [];

    return commonService.get(
      `/families/ref-data/military-info/statuses/lookup?programSponsorId=${branchFormValue.id}&componentId=${componentFormValue.id}`,
      {
        cache: { interpretHeader: false },
      },
    );
  }, [branchFormValue?.id, componentFormValue?.id]);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const orderDates = {
          orderStartDate: values.orderStartDate,
          orderEndDate: values.orderEndDate,
          orderStartDateUnknown: values.orderStartDateUnknown,
          orderEndDateUnknown: values.orderEndDateUnknown,
        };

        const updatedSpouse = await familyService.addUpdateAdult(household?.id, spouse?.id ?? null, {
          ...spouse,
          militaryDetails: {
            ...spouse?.militaryDetails,
            ...values.militaryDetails,
            ...orderDates,
          },
        });

        const _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic?.noticeError?.(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, spouse, stepConfig],
  );

  // Set initial values from application data
  useEffect(() => {
    if (spouse?.militaryDetails) {
      const {
        branch,
        militaryComponent,
        militaryStatus,
        grade,
        orderType,
        orderTitle,
        recruiter,
        recruiterType,
        paymentSystem,
        hoursPerWeek,
      } = spouse.militaryDetails;

      const orderStartDate = spouse.militaryDetails.orderStartDate
        ? new Date(spouse.militaryDetails.orderStartDate)
        : null;
      const orderEndDate = spouse.militaryDetails.orderEndDate ? new Date(spouse.militaryDetails.orderEndDate) : null;

      form.setFieldsValue({
        militaryDetails: {
          branch,
          militaryComponent,
          militaryStatus,
          grade,
          orderType,
          orderTitle,
          recruiter,
          recruiterType,
          paymentSystem,
          hoursPerWeek,
        },
        orderStartDate,
        orderEndDate,
        orderStartDateUnknown: spouse.militaryDetails.orderStartDateUnknown || false,
        orderEndDateUnknown: spouse.militaryDetails.orderEndDateUnknown || false,
      });
    } else {
      form.setFieldsValue({
        militaryDetails: {
          hoursPerWeek: 40,
        },
      });
    }
  }, [spouse, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Spouse's Military Employment" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `Tell us about ${getOtherAdultFirstName(application?.additionalParents?.[0])}'s military service`
                : 'Tell us about your military service'
            }
            className="mt-10"
          />

          <Form.Item
            name={['militaryDetails', 'hoursPerWeek']}
            label="Hours per week"
            rules={[
              { required: true, message: 'Please enter the hours per week' },
              {
                validator: (_, value) =>
                  value > 100
                    ? Promise.reject(new Error('Please verify hours (over 14 hours/day, every day)'))
                    : Promise.resolve(),
              },
            ]}
            className="mt-12 mb-8"
          >
            <InputNumber size="large" addonAfter="Hours" min={0} controls={false} className="w-48" />
          </Form.Item>

          <Form.Item
            name={['militaryDetails', 'branch']}
            label="Branch"
            rules={[{ required: true, message: 'Branch is required' }]}
          >
            <Select
              options={branchOptions}
              placeholder="Select..."
              ariaLabel="Select Branch"
              onChange={() => {
                form.setFieldsValue({
                  militaryDetails: {
                    ...form.getFieldValue('militaryDetails'),
                    militaryComponent: undefined,
                    militaryStatus: undefined,
                    grade: undefined,
                    orderType: undefined,
                    orderTitle: undefined,
                    recruiter: undefined,
                    recruiterType: undefined,
                    paymentSystem: undefined,
                  },
                });
              }}
            />
          </Form.Item>

          {spouseBranchRequiresComponent(branchFormValue?.id) && (
            <Form.Item
              name={['militaryDetails', 'militaryComponent']}
              label="Component"
              rules={[{ required: true, message: 'Component is required' }]}
            >
              <Select
                options={componentOptions}
                placeholder="Select..."
                ariaLabel="Select Component"
                disabled={!branchFormValue?.id}
                onChange={() => {
                  form.setFieldsValue({
                    militaryDetails: {
                      ...form.getFieldValue('militaryDetails'),
                      militaryStatus: undefined,
                      orderType: undefined,
                      orderTitle: undefined,
                      recruiter: undefined,
                      recruiterType: undefined,
                      paymentSystem: undefined,
                    },
                  });
                }}
              />
            </Form.Item>
          )}

          {spouseBranchRequiresComponent(branchFormValue?.id) &&
            shouldShowSpouseCurrentStatus({
              branch: branchFormValue?.title,
              componentId: componentFormValue?.id,
            }) && (
              <Form.Item
                name={['militaryDetails', 'militaryStatus']}
                label="Current Status"
                rules={[{ required: true, message: 'Current Status is required' }]}
              >
                <Select
                  options={statusOptions}
                  placeholder="Select..."
                  ariaLabel="Select Current Status"
                  disabled={!componentFormValue?.id}
                  onChange={() => {
                    form.setFieldsValue({
                      militaryDetails: {
                        ...form.getFieldValue('militaryDetails'),
                        orderType: undefined,
                        orderTitle: undefined,
                        recruiter: undefined,
                        recruiterType: undefined,
                        paymentSystem: undefined,
                      },
                    });
                  }}
                />
              </Form.Item>
            )}

          {shouldShowSpouseOrderStartDate({
            statusId: statusFormValue?.id,
          }) && (
            <div>
              <Form.Item
                name="orderStartDate"
                label="Order Start Date"
                rules={[
                  {
                    required: !dontKnowStartDateFormValue,
                    message: 'Start date is required unless unknown',
                  },
                ]}
                className="mt-4 mb-0"
              >
                <DatePicker disabled={dontKnowStartDateFormValue} />
              </Form.Item>

              <Form.Item name="orderStartDateUnknown" valuePropName="checked" noStyle>
                <Checkbox>I don't know</Checkbox>
              </Form.Item>
            </div>
          )}

          {shouldShowSpouseOrderEndDate({
            statusId: statusFormValue?.id,
          }) && (
            <>
              <Form.Item
                name="orderEndDate"
                label="Order End Date"
                rules={[
                  {
                    required: !dontKnowEndDateFormValue,
                    message: 'End date is required unless unknown',
                  },
                ]}
                className="mt-8 mb-0"
              >
                <DatePicker disabled={dontKnowEndDateFormValue} />
              </Form.Item>

              <Form.Item name="orderEndDateUnknown" valuePropName="checked" noStyle>
                <Checkbox>I don't know</Checkbox>
              </Form.Item>
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_MILITARY_EMPLOYMENT,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

// Helper function to check if spouse's branch requires component selection
export const spouseBranchRequiresComponent = (branchId) => {
  return branchId === FAMILY_BRANCH_MAP.army.id;
};

// Helper function to determine if current status should be shown
export const shouldShowSpouseCurrentStatus = ({ branch, componentId }) => {
  if (!branch || !componentId) return false;

  // Current Status applies for any military component except civilian
  if (componentId !== FAMILY_COMPONENT_MAP.civilianEmployee.id) {
    return true;
  }

  return false;
};

// Helper function to determine if order start date should be shown
export const shouldShowSpouseOrderStartDate = ({ statusId }) => {
  // Intentionally keep this function (and related Form.Item) for easy re-enabling later.
  return false;
};

// Helper function to determine if order end date should be shown
export const shouldShowSpouseOrderEndDate = ({ statusId }) => {
  // Intentionally keep this function (and related Form.Item) for easy re-enabling later.
  return false;
};
